import { clsx, type ClassValue } from "clsx";
import { extendTailwindMerge } from "tailwind-merge";

/**
 * tailwind-merge has to be taught about this system's semantic utilities.
 *
 * Out of the box it only knows the framework's own scales. This system's type roles
 * look like `text-row` / `text-legal`, which it cannot tell apart from a text
 * COLOUR — so `cn("text-primary-foreground", "text-support")` would silently
 * drop the colour and render an invisible label.
 *
 * If you add a type role, a radius alias, an easing token OR A NAMED SPACING
 * VALUE to app.css, add it here too. This is the one edit people forget.
 *
 * The four categories are not one mechanism. A type role, a radius alias and an
 * easing token are ambiguous with a COLOUR, so each is taught as a class group.
 * A named spacing value is not ambiguous with anything — the problem there is
 * the opposite one: tailwind-merge has never heard of `touch`, so it cannot see
 * that `h-touch-lg` and `h-12` are the same property and keeps BOTH, leaving
 * raw CSS source order to pick a winner. Teaching it the scale once, below,
 * fixes every spacing utility at once — width, height, size, padding, margin,
 * gap, inset — instead of thirty class-group entries.
 */
const twMerge = extendTailwindMerge({
  extend: {
    /**
     * The named spacing values from app.css: hit targets and screen gutters.
     * Added for Buddy v2 (BUD-274) in the same commit as the tokens themselves.
     * Verified rather than assumed: without this, `cn("h-12", "h-touch-lg")`
     * returns BOTH classes.
     */
    theme: {
      spacing: ["touch", "touch-lg", "touch-xl", "gutter", "gutter-lg"],
    },
    classGroups: {
      "font-size": [
        {
          text: [
            "mega", "hero", "display", "title", "section", "row", "support", "label", "legal",
            // Added for Buddy v2 (BUD-274) in the same commit as the role in app.css.
            // Without this line `cn("text-foreground-faint", "text-eyebrow")` drops one
            // of the two and the label renders at the wrong size or in the wrong colour.
            "eyebrow",
          ],
        },
      ],
      rounded: [{ rounded: ["pill", "card", "field", "tile", "inline"] }],
      ease: [{ ease: ["out-ds", "in-out-ds"] }],
    },
  },
});

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}
